import keys from '@debbie/common/dist/cache/keys.js';
import { CreditorWebhookEvent, HookItemState, } from '@debbie/common/dist/hooks/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { sortBy, uniqueBy } from '@debbie/common/dist/util/index.js';
import * as webhooks from '@debbie/cortex/dist/api/hooks.js';
import { hooksEntity, useHookItems, useHooks, } from '@debbie/cortex/dist/hooks/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import q from '@debbie/cortex/dist/query.js';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { Size } from '../../design-system.js';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import { Col, Row } from '../Grid/index.js';
import { PlusCircle, XCircle } from '../Icons/index.js';
import X from '../Icons/X.js';
import { Badge, Button, Checkbox, CopyToClipboard, Input, Modal, Panel, PlaceholderLoader, Well, } from '../index.js';
import RemoteTable from '../RemoteTable.js';
import { useCreditorScope } from './creditorScope.js';
import HookCreditor from './HookCreditor.js';
const stateLabels = (lang) => ({
    [HookItemState.FailedAndNotified]: lang.webhookSettings.statePermanentlyFailed,
    [HookItemState.FailedAwatingNotification]: lang.webhookSettings.statePermanentlyFailed,
    [HookItemState.FailedNoNotification]: lang.webhookSettings.statePermanentlyFailed,
    [HookItemState.Pending]: lang.webhookSettings.stateAwaiting,
    [HookItemState.Success]: lang.webhookSettings.stateSucceeded,
});
const tenantEventTypeGroups = [
    { eventTypes: ['updates.create', 'updates.update', 'updates.delete'] },
    {
        eventTypes: ['creditors.create', 'creditors.update'],
    },
    {
        eventTypes: [
            'case-vouchers.create',
            'case-vouchers.update',
            'case-vouchers.delete',
        ],
    },
    {
        eventTypes: [
            'deposits.created',
            'deposits.booked',
            'deposits.moved',
            'deposits.unbooked',
            'deposits.deleted',
            'deposits.restored',
        ],
    },
    {
        eventTypes: [
            'payment-plans.create',
            'payment-plans.update',
            'payment-plans.delete',
        ],
    },
    { eventTypes: ['interactions.send'] },
    { eventTypes: ['customers.create', 'customers.update', 'customers.delete'] },
    {
        eventTypes: [
            'users-on-customers.create',
            'users-on-customers.update',
            'users-on-customers.delete',
        ],
    },
    {
        eventTypes: [
            'cases.create',
            'cases.update',
            'cases.delete',
            'cases.merge',
            'cases.undo-merge',
        ],
    },
    { eventTypes: ['billings.create'] },
    { eventTypes: ['payouts.create'] },
];
// Creditors only subscribe to the events that are routed by creditor — see
// CreditorWebhookEvent. The backend enforces the same list.
const creditorEventTypeGroups = [
    {
        eventTypes: [
            CreditorWebhookEvent.CaseCreated,
            CreditorWebhookEvent.CaseUpdated,
            CreditorWebhookEvent.CaseDeleted,
        ],
    },
    { eventTypes: [CreditorWebhookEvent.BillingCreated] },
];
const Webhooks = (props) => {
    const lang = useSelector((s) => s.lang);
    const { clientSite, creditorId } = useCreditorScope();
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const states = stateLabels(lang);
    return (React.createElement(React.Fragment, null,
        React.createElement(Panel, { header: React.createElement("div", null,
                lang.navigation.settingsWebhooks,
                ' ',
                React.createElement(Button, { style: { float: 'right' }, onClick: () => setSettingsModalOpen(!settingsModalOpen), size: "mini" }, lang.webhookSettings.webhooks)) },
            React.createElement(RemoteTable, { useFetch: (filters, page, pageSize) => useHookItems(creditorId, filters, page, pageSize), initFilters: {}, genKey: (p) => p.id, sortKeyKey: "state", sortDirectionKey: "state", pageSize: 100, onClick: (hookItem) => c.redirect.redirect(`${props.basePath}/${hookItem.id}`), values: {
                    event: {
                        label: lang.webhookSettings.columnEvent,
                        sortable: false,
                        map: renderHookEvent,
                    },
                    name: {
                        label: lang.webhookSettings.columnWebhook,
                        sortable: false,
                        map: renderHookName,
                    },
                    ...(clientSite
                        ? {}
                        : {
                            creditorId: {
                                label: lang.webhookSettings.columnCreditor,
                                sortable: false,
                                map: renderHookCreditor,
                            },
                        }),
                    url: {
                        label: lang.webhookSettings.columnUrl,
                        sortable: false,
                        map: renderHookUrl,
                    },
                    createdAt: {
                        label: lang.webhookSettings.columnDate,
                        sortable: false,
                        map: renderHookDate,
                    },
                    state: {
                        label: lang.webhookSettings.columnState,
                        sortable: false,
                        map: (state) => renderHookState(states, state),
                        filter: (filterProps) => renderHookStateFilter(lang, filterProps),
                        filterActive: (filter) => filter.state != null && filter.state.length > 0,
                    },
                } }),
            settingsModalOpen && (React.createElement(HooksSettings, { onToggle: () => setSettingsModalOpen(!settingsModalOpen) })))));
};
const StateFilterCheckbox = (props) => (React.createElement(Checkbox, { checkMark: true, size: Size.Small, setting: true, onChange: (e) => {
        if (e.target.checked) {
            props.setFilter({
                state: addToFilter(props.filter.state, props.states),
            });
        }
        else {
            props.setFilter({
                state: removeFromFilter(props.filter.state, props.states),
            });
        }
    }, checked: !!props.filter.state?.includes(props.states[0]), placeholder: props.label }));
const addToFilter = (state, add) => {
    return uniqueBy([...(state ?? []), ...add], (item) => item);
};
const removeFromFilter = (state, remove) => {
    const newState = (state ?? []).filter((item) => !remove.includes(item));
    return newState.length === 0 ? undefined : newState;
};
const HooksSettings = (props) => {
    const lang = useSelector((s) => s.lang);
    const { clientSite, creditorId } = useCreditorScope();
    const hooksLoadable = useHooks(creditorId);
    const hooks = h.hasData(hooksLoadable) ? hooksLoadable.data : null;
    const eventTypeGroups = clientSite
        ? creditorEventTypeGroups
        : tenantEventTypeGroups;
    const [webhookName, setWebhookName] = useState('');
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookEmail, setWebhookEmail] = useState(null);
    const [events, setEvents] = useState([]);
    const [webhookHeaders, setWebhookHeaders] = useState(null);
    const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
    const [deepFlattenBody, setDeepFlattenBody] = useState(false);
    const [pull, setPull] = useState(false);
    const createWebhookMutation = useMutation(async () => {
        await webhooks.create({
            name: webhookName,
            url: webhookUrl,
            events,
            creditorId,
            email: webhookEmail || undefined,
            headers: webhookHeaders,
            deepFlattenBody,
            pull,
        });
        q.invalidate(keys.write.create(hooksEntity));
        setWebhookName('');
        setWebhookUrl('');
        setWebhookHeaders(null);
        setEvents([]);
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.webhook);
        },
    });
    return (React.createElement(Modal, { header: lang.navigation.settingsWebhooks, onToggle: props.onToggle },
        React.createElement(Well, null,
            React.createElement(Input, { placeholder: lang.webhookSettings.namePlaceholder, value: webhookName, onChange: (e) => {
                    setWebhookName(e.target.value);
                } }),
            React.createElement(Input, { placeholder: lang.webhookSettings.urlPlaceholder, value: webhookUrl, onChange: (e) => {
                    setWebhookUrl(e.target.value);
                } }),
            React.createElement(Input, { placeholder: lang.webhookSettings.emailPlaceholder, value: webhookEmail || '', onChange: (e) => {
                    setWebhookEmail(e.target.value != '' ? e.target.value : null);
                } }),
            React.createElement("div", null, eventTypeGroups.map(({ eventTypes }) => (React.createElement(EventTypeGroup, { key: eventTypes[0] }, eventTypes.map((eventType) => (React.createElement(EventCheckbox, { key: eventType, onChange: (_events) => {
                    setEvents(_events);
                }, events: events, event: eventType }))))))),
            advancedSettingsOpen ? (React.createElement("div", null,
                React.createElement("h3", null, lang.webhookSettings.requestHeaders),
                React.createElement(RequestHeaderEditor, { headers: webhookHeaders, onChange: (newHeaders) => setWebhookHeaders(newHeaders) }),
                React.createElement("h3", null, lang.webhookSettings.otherSettings),
                React.createElement(Checkbox, { placeholder: lang.webhookSettings.deepFlatten, checked: deepFlattenBody, onChange: (e) => setDeepFlattenBody(e.target.checked) }),
                !clientSite && (React.createElement(Checkbox, { placeholder: lang.webhookSettings.pullWebhooks, checked: pull, onChange: (e) => setPull(e.target.checked) })))) : (React.createElement("p", { style: {
                    color: THEME.COLOR_ACCENT,
                    fontWeight: '900',
                    cursor: 'pointer',
                    fontSize: '0.8em',
                    marginBottom: '2em',
                }, onClick: () => setAdvancedSettingsOpen(!advancedSettingsOpen) }, lang.webhookSettings.advancedSettings)),
            React.createElement(Button, { disabled: !(webhookName &&
                    (pull ||
                        /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-/]))?/.test(webhookUrl)) &&
                    events.length > 0), loading: createWebhookMutation.isLoading, onClick: () => createWebhookMutation.mutate() }, lang.webhookSettings.addWebhook)),
        hooks && hooks.length === 0 ? (React.createElement("h3", null, lang.webhookSettings.noWebhooksYet)) : hooks && hooks.length > 0 ? (sortBy(hooks, (hook) => new Date(hook.createdAt).getTime(), {
            reverse: true,
        }).map((hook) => (React.createElement(Hook, { key: hook.id, hook: hook, showCreditor: !clientSite })))) : (React.createElement(PlaceholderLoader, null))));
};
const EventCheckbox = (props) => {
    return (React.createElement("div", { style: { marginRight: '10px' } },
        React.createElement(Checkbox, { placeholder: props.event, checked: !!props.events.find((e) => e === props.event), checkMark: true, size: Size.Small, onChange: (e) => {
                const checked = e.target.checked;
                props.onChange(checked
                    ? props.events.concat(props.event)
                    : props.events.filter((_e) => _e !== props.event));
            } })));
};
const Hook = (props) => {
    const { hook } = props;
    const lang = useSelector((s) => s.lang);
    const deleteWebhookMutation = useMutation(async () => {
        await webhooks.del(hook.id);
        q.invalidate(keys.write.modify(hooksEntity, hook.id));
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'delete', lang.errorEntities.webhook);
        },
    });
    return (React.createElement(Well, { style: { marginTop: `${THEME.GRID_SPACING}px` } },
        React.createElement("h3", null,
            React.createElement(CopyToClipboard, { text: hook.id }, hook.name),
            ' ',
            React.createElement("small", null, moment(hook.createdAt).format(THEME.DATE_FORMAT)),
            React.createElement("span", { onClick: () => deleteWebhookMutation.mutate(), style: {
                    float: 'right',
                    cursor: deleteWebhookMutation.isLoading ? 'wait' : 'pointer',
                    opacity: deleteWebhookMutation.isLoading ? 0.5 : 1,
                } },
                React.createElement(X, null))),
        props.showCreditor && (React.createElement("p", null,
            React.createElement(HookCreditor, { creditorId: hook.creditorId }))),
        React.createElement("p", null, hook.url),
        React.createElement("p", null, hook.email),
        hook.pull ? (React.createElement("p", null, lang.webhookSettings.pullWebhook)) : (React.createElement(Input, { placeholder: lang.webhookSettings.verificationToken, value: hook.verificationToken, disabled: true })),
        React.createElement("p", { style: { marginTop: 0, wordWrap: 'break-word', lineHeight: '1.6em' } }, hook.events.map((event) => (React.createElement(Badge, { key: event, theme: THEME.COLOR_ACCENT, style: { marginRight: '5px' } }, event))))));
};
const EventTypeGroup = styled.div `
  margin-bottom: ${THEME.GRID_SPACING}px;
`;
const RequestHeaderEditor = (props) => {
    const lang = useSelector((s) => s.lang);
    const { headers, onChange } = props;
    const [newHeaderKey, setNewHeaderKey] = useState('');
    const [newHeaderValue, setNewHeaderValue] = useState('');
    return (React.createElement("div", null,
        headers &&
            Object.entries(headers).map(([key, value]) => (React.createElement("div", { key: key, style: { marginBottom: '15px' } },
                React.createElement(XCircle, { onClick: () => {
                        const newHeaders = { ...headers };
                        delete newHeaders[key];
                        onChange(newHeaders);
                    }, style: {
                        margin: '0.2em 0 -0.2em 0',
                        cursor: 'pointer',
                    } }),
                ' ',
                key,
                ": ",
                React.createElement("b", null, value)))),
        React.createElement(Row, null,
            React.createElement(Col, { width: "4" },
                React.createElement(Input, { placeholder: lang.webhookSettings.headerKeyPlaceholder, onChange: (e) => setNewHeaderKey(e.target.value), value: newHeaderKey })),
            React.createElement(Col, { width: "4" },
                React.createElement(Input, { placeholder: lang.webhookSettings.headerValuePlaceholder, onChange: (e) => setNewHeaderValue(e.target.value), value: newHeaderValue })),
            React.createElement(Col, { width: "4" },
                React.createElement(Button, { disabled: !(newHeaderKey && newHeaderValue), onClick: () => {
                        onChange({
                            ...headers,
                            [newHeaderKey]: newHeaderValue,
                        });
                        setNewHeaderKey('');
                        setNewHeaderValue('');
                    } },
                    React.createElement(PlusCircle, { style: {
                            margin: '0.2em 0 -0.2em 0',
                        } }),
                    ' ',
                    lang.webhookSettings.addHeader)))));
};
export default Webhooks;
const renderHookEvent = (event) => (React.createElement(Badge, { theme: THEME.COLOR_ACCENT }, event));
const renderHookName = (name) => React.createElement("b", null, name);
const renderHookCreditor = (hookCreditorId) => (React.createElement(HookCreditor, { creditorId: hookCreditorId }));
const renderHookUrl = (url) => (React.createElement("div", null, url.length > 63 ? `${url.slice(0, 20)}(...)${url.slice(-40)}` : url));
const renderHookDate = (createdAt) => (React.createElement("div", null, moment(createdAt).format(THEME.DATE_FORMAT_LONG)));
const renderHookState = (states, state) => (React.createElement("div", null,
    React.createElement(Badge, { theme: [HookItemState.Success, HookItemState.Pending].includes(state)
            ? THEME.SUCCESS
            : THEME.DANGEROUS }, states[state])));
const renderHookStateFilter = (lang, filterProps) => {
    return (React.createElement("div", null,
        React.createElement(StateFilterCheckbox, { label: lang.webhookSettings.stateAwaiting, states: [HookItemState.Pending], filter: filterProps.filter, setFilter: filterProps.setFilter }),
        React.createElement(StateFilterCheckbox, { label: lang.webhookSettings.statePermanentlyFailed, states: [
                HookItemState.FailedAndNotified,
                HookItemState.FailedAwatingNotification,
                HookItemState.FailedNoNotification,
            ], filter: filterProps.filter, setFilter: filterProps.setFilter }),
        React.createElement(StateFilterCheckbox, { label: lang.webhookSettings.stateSucceeded, states: [HookItemState.Success], filter: filterProps.filter, setFilter: filterProps.setFilter })));
};
//# sourceMappingURL=Webhooks.js.map